import { FunctionComponent } from 'react'
import Skeleton from 'react-loading-skeleton'
import { Box, Typography, Tooltip, LinearProgress } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import EmptyStateAction, { EmptyAction } from './EmptyStateAction'
import { Info, Award } from 'react-feather'
import colors from '../../colors'
import { SPACING } from '../../constants'
import { TopBenefit } from '../../types'

type TopBenefitsCardProps = {
  emptyAction?: EmptyAction,
  benefits: TopBenefit[],
  loading: boolean,
}

const TopBenefitsCard: FunctionComponent<TopBenefitsCardProps> = ({ benefits, loading, emptyAction }) => {
  const styles = useStyles()

  const maxRedemptions = benefits.length > 0 ? Math.max(...benefits.map(b => b.redemptionCount)) : 0
  const InfoIcon: FunctionComponent = () => {
    return (
      <Box className={styles.infoContainer}>
        <Tooltip title="Most redeemed benefits in the last 30 days">
          <Info size={16} strokeWidth={3} color={colors.GREY_3} />
        </Tooltip>
      </Box>
    )
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.titleContainer}>
        <Award size={20} color={colors.GREY_3} style={{ marginRight: SPACING }} />
        <Typography variant="h6">
          Top Benefits
        </Typography>
        <InfoIcon />
      </Box>

      {loading ? (
        <Box mt={2}>
          <Skeleton count={5} height={60} />
        </Box>
      ) : benefits.length === 0 ? (
        <Box className={styles.emptyState}>
          <Typography variant="body2" color="textSecondary">
            No benefits redeemed yet.
          </Typography>
          <EmptyStateAction action={emptyAction} />
        </Box>
      ) : (
        <Box mt={2}>
          {benefits.map((benefit, index) => (
            <Box key={benefit.benefitId} mb={3}>
              <Box className={styles.benefitHeader}>
                <Box className={styles.rank}>
                  #{index + 1}
                </Box>
                <Box flex={1}>
                  <Typography variant="body1" className={styles.benefitTitle}>
                    {benefit.benefitTitle}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {benefit.redemptionCount} {benefit.redemptionCount === 1 ? 'redemption' : 'redemptions'}
                  </Typography>
                </Box>
                <Typography variant="h6" className={styles.count}>
                  {benefit.redemptionCount}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(benefit.redemptionCount / maxRedemptions) * 100}
                className={styles.progressBar}
              />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

const useStyles = makeStyles({
  container: {
    borderRadius: 12,
    boxShadow: '0px 0px 4px rgba(17, 51, 85, 0.15)',
    padding: SPACING * 3,
    backgroundColor: '#fff',
    width: '100%',
    minWidth: 0,
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: SPACING * 2,
  },
  infoContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 'auto',
    cursor: 'pointer',
  },
  benefitHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: SPACING,
  },
  rank: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    backgroundColor: '#f5f5f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    fontSize: '0.875rem',
    color: colors.GREY_3,
    marginRight: SPACING * 2,
  },
  benefitTitle: {
    fontWeight: 500,
    marginBottom: 4,
  },
  count: {
    fontWeight: 600,
    color: '#1976d2',
    marginLeft: SPACING * 2,
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    backgroundColor: '#e0e0e0',
    '& .MuiLinearProgress-bar': {
      backgroundColor: '#1976d2',
      borderRadius: 4,
    },
  },
  emptyState: {
    padding: SPACING * 4,
    display: 'flex',
    // Column, so an empty-state action sits under the message rather than
    // running on from the end of the sentence.
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 2,
    textAlign: 'center',
  },
})

export default TopBenefitsCard
